<script lang="ts">
  import { onMount } from "svelte";
  import Navbar from "@/components/appDriver/Navbar.svelte";
  import { currentPage } from "@/components/appDriver/store";
  import PageHome from "@/components/appDriver/PageHome.svelte";
  import TaxHistoryItem from "@/components/appDriver/driverTax/TaxHistoryItem.svelte";
  import { axiosInstance } from "@/plugins/axios";
  import TaxHistorySkeleton from "@/components/appDriver/driverTax/TaxHistorySkeleton.svelte";

  let data:any = [];
  let isLoading: boolean = false;

  // Pagination State
  let page = 1;
  let hasNextPage = false;
  let scrollContainer: HTMLElement;

  onMount(() => {
    $currentPage = "driver-tax-list";
    getDriverTaxHistory(1);
  });

  async function getDriverTaxHistory(pageToLoad: number) {
    if (isLoading) return;
    if (!hasNextPage && pageToLoad > 1) return;
    try {
      isLoading = true;
      const { data: response } = await axiosInstance.get(
        `/api/v1/driverTaxFile/myDriverTaxFiles?page=${pageToLoad}&limit=10`,
        {
          baseURL: import.meta.env.VITE_API_LOCA_URL,
        },
      );
      if (response && response.data) {
        const newItems = response.data;
        const pagination = response.pagination;

        if (pageToLoad === 1) {
          data = newItems;
        } else {
          data = [...data, ...newItems];
        }
        page = pagination.currentPage;
        hasNextPage = pagination.hasNextPage;
        console.log("Fetched page:", page, "Has next page:", hasNextPage);
      }
    } catch (error) {
      console.error("Error fetching driver tax history:", error);
    } finally {
      isLoading = false;
    }
  }

  const handleScroll = () => {
    if (!scrollContainer) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    // Check if we are near the bottom (within 50px)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      if (hasNextPage && !isLoading) {
        getDriverTaxHistory(page + 1);
      }
    }
  }

  type GroupedData = { title: string; items: typeof data };
  let groupedHistory: GroupedData[] = [];

  $: {
    const groups: Record<string, typeof data> = {};

    data.forEach((item) => {
      const dateObj = new Date(item.year, item.month - 1);
      const headerTitle = dateObj.toLocaleDateString('en-GB', { 
         month: '2-digit', 
         year: 'numeric' 
      });

      if (!groups[headerTitle]) {
        groups[headerTitle] = [];
      }
      groups[headerTitle].push(item);
    });

    groupedHistory = Object.entries(groups)
      .map(([title, items]) => ({
        title,
        items,
      }))
      .sort((a, b) => {
        const yearDiff = b.items[0].year - a.items[0].year;
        if (yearDiff !== 0) return yearDiff;
        return b.items[0].month - a.items[0].month;
      });
}
</script>

<div class="page flex flex-col h-full bg-[#EFEFEF]">
  <Navbar
    showBack={true}
    on:back={() =>
      PubSub.publish("DRIVER.CHANGE_PAGE", { component: PageHome })}
  />
  <img
    src={new URL("@/assets/general/loca-logo-horizontal.png", import.meta.url)
      .href}
    width="100"
    class="-mt-10 mx-auto"
  />
  <div class="text-xl text-center font-semibold m-4 mt-6">ໃບແຈ້ງມອບອາກອນ</div>
  <main class="flex-1 overflow-y-scroll px-4 space-y-4 pb-4" bind:this={scrollContainer} on:scroll={handleScroll}>
    {#if isLoading && page === 1}
    <div class="relative font-semibold flex items-center mb-2 mt-4">
      <div class="h-4 bg-gray-300 rounded w-20 animate-pulse"></div>
    </div>
    {#each Array(5) as _}
      <TaxHistorySkeleton />
    {/each}

  {:else if data.length === 0 && !isLoading}
    <div class="flex flex-col items-center justify-center h-64 text-gray-500">
      <p>No tax history found.</p>
    </div>

  {:else}
    {#each groupedHistory as group}
      <div class="relative font-semibold flex items-center mb-2 mt-4">
        <p>{group.title}</p>
      </div>
      {#each group.items as item (item._id)}
        <TaxHistoryItem {item} MonthAndYear={group} />
      {/each}
    {/each}

    {#if isLoading && page > 1}
      <div class="py-4 space-y-4">
           <TaxHistorySkeleton />
      </div>
    {/if}

    {#if !hasNextPage && data.length > 0}
      <div class="text-center text-gray-400 text-sm py-4">
         All history loaded
      </div>
    {/if}
  {/if}
  </main>
</div>
